import type { Citation } from '../orchestration/types';

export type CitationStyle =
  | 'bibtex'
  | 'apa'
  | 'mla'
  | 'chicago'
  | 'vancouver';

/**
 * Pure serializers — take a list of `Citation` and produce a single
 * string in the requested style. Used by:
 *   - Citations page Export menu (downloads + clipboard copy).
 *   - Manuscript bibliography block (Phase 3, Vancouver default).
 *
 * All formatters are forgiving: missing authors / year / journal
 * degrade gracefully rather than throwing. Citations without a
 * usable identifier are skipped entirely.
 */
export function formatCitations(
  citations: Citation[],
  style: CitationStyle
): string {
  const usable = citations.filter(c => c.identifier);
  switch (style) {
    case 'bibtex':
      return usable.map(toBibTeX).join('\n\n');
    case 'apa':
      return usable.map((c, i) => `${i + 1}. ${toAPA(c)}`).join('\n');
    case 'mla':
      return usable.map((c, i) => `${i + 1}. ${toMLA(c)}`).join('\n');
    case 'chicago':
      return usable.map((c, i) => `${i + 1}. ${toChicago(c)}`).join('\n');
    case 'vancouver':
      return usable.map((c, i) => `${i + 1}. ${toVancouver(c)}`).join('\n');
  }
}

export function citationFileExtension(style: CitationStyle): string {
  return style === 'bibtex' ? 'bib' : 'txt';
}

// ---------- BibTeX ----------

function toBibTeX(c: Citation): string {
  const key = bibtexKey(c);
  const entryType = c.type === 'arxiv' ? 'misc' : 'article';
  const fields: Array<[string, string | undefined]> = [
    ['title', c.title],
    ['author', c.authors?.join(' and ')],
    ['journal', c.journal],
    ['year', c.year?.toString()],
    ['url', c.url],
  ];
  if (c.type === 'pubmed') fields.push(['pmid', c.identifier]);
  if (c.type === 'doi') fields.push(['doi', c.identifier]);
  if (c.type === 'arxiv') fields.push(['eprint', c.identifier]);

  const body = fields
    .filter(([, v]) => v)
    .map(([k, v]) => `  ${k} = {${escapeBibTeX(v as string)}}`)
    .join(',\n');
  return `@${entryType}{${key},\n${body}\n}`;
}

function bibtexKey(c: Citation): string {
  const firstAuthor = c.authors?.[0]?.split(/\s+/).pop() ?? 'anon';
  const year = c.year ?? 'nd';
  const slug = `${firstAuthor}${year}${c.type}${c.identifier}`
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 64);
  return slug || 'ref';
}

function escapeBibTeX(s: string): string {
  return s.replace(/([\\{}])/g, '\\$1');
}

// ---------- APA ----------

function toAPA(c: Citation): string {
  const authors = formatAuthorsAPA(c.authors);
  const year = c.year ? ` (${c.year})` : '';
  const title = c.title ? `. ${stripTrailingPeriod(c.title)}` : '';
  const journal = c.journal ? `. ${italic(c.journal)}` : '';
  const id = identifierSuffix(c);
  return `${authors}${year}${title}${journal}${id}`.trim();
}

function formatAuthorsAPA(authors?: string[]): string {
  if (!authors?.length) return 'Anonymous';
  if (authors.length <= 20) return authors.join(', ');
  const firstNineteen = authors.slice(0, 19).join(', ');
  const last = authors[authors.length - 1];
  return `${firstNineteen}, … ${last}`;
}

// ---------- MLA ----------

function toMLA(c: Citation): string {
  const authors = c.authors?.join(', ') ?? 'Anonymous';
  const title = c.title ? ` "${stripTrailingPeriod(c.title)}."` : '';
  const journal = c.journal ? ` ${italic(c.journal)},` : '';
  const year = c.year ? ` ${c.year},` : '';
  const id = identifierSuffix(c);
  return `${authors}.${title}${journal}${year}${id}`.trim();
}

// ---------- Chicago (author-date) ----------

function toChicago(c: Citation): string {
  const authors = c.authors?.join(', ') ?? 'Anonymous';
  const year = c.year ? ` ${c.year}.` : '';
  const title = c.title ? ` "${stripTrailingPeriod(c.title)}."` : '';
  const journal = c.journal ? ` ${italic(c.journal)}.` : '';
  const id = identifierSuffix(c);
  return `${authors}.${year}${title}${journal}${id}`.trim();
}

// ---------- Vancouver (numbered; closest to our existing sources block) ----------

function toVancouver(c: Citation): string {
  const authors = formatAuthorsVancouver(c.authors);
  const title = c.title ? ` ${stripTrailingPeriod(c.title)}.` : '';
  const journal = c.journal ? ` ${c.journal}.` : '';
  const year = c.year ? ` ${c.year}.` : '';
  const id = identifierSuffix(c);
  const preprintNote = c.type === 'arxiv' ? ' [preprint]' : '';
  return `${authors}${preprintNote}.${title}${journal}${year}${id}`.trim();
}

function formatAuthorsVancouver(authors?: string[]): string {
  if (!authors?.length) return 'Anonymous';
  if (authors.length <= 6) return authors.join(', ');
  return `${authors.slice(0, 6).join(', ')}, et al.`;
}

// ---------- Shared helpers ----------

function identifierSuffix(c: Citation): string {
  switch (c.type) {
    case 'pubmed':
      return ` PMID: ${c.identifier}.`;
    case 'doi':
      return ` DOI: ${c.identifier}.`;
    case 'arxiv':
      return ` arXiv: ${c.identifier}.`;
    default:
      return c.url ? ` ${c.url}` : '';
  }
}

function stripTrailingPeriod(s: string): string {
  return s.replace(/\.\s*$/, '');
}

function italic(s: string): string {
  // Plain-text render; callers that need real italics (e.g. HTML /
  // DOCX export) should post-process. Keeping it raw avoids leaking
  // markdown asterisks into copied BibTeX/plain-text output.
  return s;
}
